import { create, all, type MathNode } from 'mathjs';

// Create a math.js instance with all functions
const math = create(all);

const isMathUnit = (value: unknown): value is {
    toNumber: () => number;
    formatUnits: () => string;
    simplify: () => { toNumber: () => number; formatUnits: () => string };
} => {
    return Boolean(
        value &&
        typeof value === 'object' &&
        'toNumber' in value &&
        typeof (value as { toNumber?: unknown }).toNumber === 'function' &&
        'formatUnits' in value &&
        typeof (value as { formatUnits?: unknown }).formatUnits === 'function' &&
        'simplify' in value &&
        typeof (value as { simplify?: unknown }).simplify === 'function'
    );
};

/**
 * Unit-preserving wrapper for math.ceil
 * Preserves units when the input has units, otherwise works normally
 */
const ceilWithUnits = (value: any): any => {
    // Check if it's a math.js Unit object
    if (isMathUnit(value)) {
        const numericValue = value.toNumber();
        const unitString = value.formatUnits();
        const rounded = Math.ceil(numericValue);
        return math.unit(rounded, unitString);
    }
    // For non-unit values, use math.ceil normally
    return math.ceil(value);
};

/**
 * Unit-preserving wrapper for math.floor
 * Preserves units when the input has units, otherwise works normally
 */
const floorWithUnits = (value: any): any => {
    // Check if it's a math.js Unit object
    if (isMathUnit(value)) {
        const numericValue = value.toNumber();
        const unitString = value.formatUnits();
        const rounded = Math.floor(numericValue);
        return math.unit(rounded, unitString);
    }
    // For non-unit values, use math.floor normally
    return math.floor(value);
};

/**
 * Unit-preserving wrapper for math.round
 * Preserves units when the input has units, otherwise works normally
 */
const roundWithUnits = (value: any): any => {
    // Check if it's a math.js Unit object
    if (isMathUnit(value)) {
        const numericValue = value.toNumber();
        const unitString = value.formatUnits();
        const rounded = Math.round(numericValue);
        return math.unit(rounded, unitString);
    }
    // For non-unit values, use math.round normally
    return math.round(value);
};

const normalizeVariadicArgs = (args: any[]): any[] => {
    if (args.length === 1 && Array.isArray(args[0])) {
        return args[0];
    }
    return args;
};

const coerceNumbersToPrimaryUnit = (args: any[]): any[] => {
    const normalizedArgs = normalizeVariadicArgs(args);
    const unitArg = normalizedArgs.find(isMathUnit);
    if (!unitArg) return normalizedArgs;

    const primaryUnit = unitArg.formatUnits();
    return normalizedArgs.map((arg) => {
        if (typeof arg === 'number') {
            return math.unit(arg, primaryUnit);
        }
        return arg;
    });
};

const maxWithUnits = (...args: any[]): any => {
    const coercedArgs = coerceNumbersToPrimaryUnit(args);
    return (math.max as (...values: any[]) => any)(...coercedArgs);
};

const minWithUnits = (...args: any[]): any => {
    const coercedArgs = coerceNumbersToPrimaryUnit(args);
    return (math.min as (...values: any[]) => any)(...coercedArgs);
};

// Create a math object with unit-preserving wrappers for helpers that need
// explicit handling when units are mixed with numeric literals.
const mathWithUnits = {
    ...math,
    ceil: ceilWithUnits,
    floor: floorWithUnits,
    round: roundWithUnits,
    max: maxWithUnits,
    min: minWithUnits,
};

const ALLOWED_FORMULA_FUNCTIONS = new Set([
    'abs',
    'ceil',
    'floor',
    'round',
    'min',
    'max',
    'pow',
    'sqrt',
    'log',
    'exp'
]);

const ALLOWED_FORMULA_OPERATORS = new Set([
    '+',
    '-',
    '*',
    '/',
    '%',
    '^',
    'unaryPlus',
    'unaryMinus',
    '<',
    '<=',
    '>',
    '>=',
    '==',
    '!=',
    'and',
    'or',
    'not'
]);

const SAFE_FORMULA_FUNCTIONS = Object.fromEntries(
    Array.from(ALLOWED_FORMULA_FUNCTIONS).map((name) => [
        name,
        mathWithUnits[name as keyof typeof mathWithUnits]
    ])
);

// Expose math.js functions in expressions instead of native Math
// math.js functions work with units, unlike native Math
// We use mathWithUnits which includes unit-preserving wrappers for ceil, floor, and round
const JAVASCRIPT_BUILTINS: Record<string, unknown> = Object.freeze({
    math: mathWithUnits,
    Infinity,
    NaN,
    ...SAFE_FORMULA_FUNCTIONS
});

export const normalizeFormulaExpressionForMath = (expression: string): string =>
    expression
        .replace(/\*\*/g, '^')
        .replace(/&&/g, ' and ')
        .replace(/\|\|/g, ' or ')
        .replace(/!(?!=)/g, ' not ');

const getAccessorPropertyName = (node: MathNode): string | null => {
    const record = node as unknown as {
        index?: {
            dimensions?: Array<{ value?: unknown }>;
        };
    };
    const value = record.index?.dimensions?.[0]?.value;
    return typeof value === 'string' ? value : null;
};

const isAllowedFunctionNode = (node: MathNode): boolean => {
    const record = node as unknown as {
        fn?: MathNode;
        name?: string;
    };

    if (!record.fn) {
        return false;
    }

    if (record.fn.type === 'SymbolNode' && record.name) {
        return ALLOWED_FORMULA_FUNCTIONS.has(record.name);
    }

    if (record.fn.type !== 'AccessorNode') {
        return false;
    }

    const accessor = record.fn as unknown as {
        object?: { type?: string; name?: string };
    };
    const propertyName = getAccessorPropertyName(record.fn);

    return (
        accessor.object?.type === 'SymbolNode' &&
        accessor.object.name === 'math' &&
        Boolean(propertyName && ALLOWED_FORMULA_FUNCTIONS.has(propertyName))
    );
};

export const validateSafeFormulaExpression = (expression: string): { valid: boolean; error?: string } => {
    let parsed: MathNode;
    try {
        parsed = math.parse(normalizeFormulaExpressionForMath(expression));
    } catch (error) {
        return {
            valid: false,
            error: `Failed to parse expression: ${(error as Error).message}`
        };
    }

    let issue: string | undefined;
    parsed.traverse((node, _path, parent) => {
        if (issue) {
            return;
        }

        switch (node.type) {
            case 'OperatorNode': {
                const op = (node as unknown as { op?: string }).op;
                if (!op || !ALLOWED_FORMULA_OPERATORS.has(op)) {
                    issue = `Operator "${op ?? node.toString()}" is not allowed.`;
                }
                return;
            }
            case 'ConstantNode': {
                const value = (node as unknown as { value?: unknown }).value;
                if (typeof value === 'string' && parent?.type !== 'IndexNode') {
                    issue = 'String literals are not allowed in formulas.';
                }
                return;
            }
            case 'SymbolNode': {
                const name = (node as unknown as { name?: string }).name;
                if (
                    name === 'constructor' ||
                    name === 'prototype' ||
                    name === 'eval' ||
                    name === 'Function' ||
                    name === 'import' ||
                    name === 'process' ||
                    name === 'window' ||
                    name === 'globalThis'
                ) {
                    issue = `Identifier "${name}" is not allowed.`;
                }
                return;
            }
            case 'FunctionNode': {
                if (!isAllowedFunctionNode(node)) {
                    issue = `Function "${(node as unknown as { name?: string }).name ?? node.toString()}" is not allowed.`;
                }
                return;
            }
            case 'AccessorNode': {
                if (parent?.type !== 'FunctionNode' || (parent as unknown as { fn?: MathNode }).fn !== node) {
                    issue = 'Property access is not allowed in formulas.';
                    return;
                }

                const object = node as unknown as { object?: { type?: string; name?: string } };
                const propertyName = getAccessorPropertyName(node);
                if (
                    object.object?.type !== 'SymbolNode' ||
                    object.object.name !== 'math' ||
                    !propertyName ||
                    !ALLOWED_FORMULA_FUNCTIONS.has(propertyName)
                ) {
                    issue = 'Only approved math helper calls are allowed.';
                }
                return;
            }
            case 'IndexNode':
            case 'ParenthesisNode':
                return;
            default:
                issue = `${node.type.replace(/Node$/, '')} expressions are not allowed.`;
        }
    });

    return issue ? { valid: false, error: issue } : { valid: true };
};

export const evaluateSafeFormulaExpression = (
    expression: string,
    scope: Record<string, unknown>
): unknown => {
    const normalizedExpression = normalizeFormulaExpressionForMath(expression);
    const validation = validateSafeFormulaExpression(normalizedExpression);
    if (!validation.valid) {
        throw new Error(validation.error ?? 'Formula expression is not allowed.');
    }

    const parsed = math.parse(normalizedExpression);
    return parsed.evaluate({
        ...JAVASCRIPT_BUILTINS,
        ...scope
    });
};

export const compileSafeFormulaExpression = (expression: string): ((scope: Record<string, unknown>) => unknown) => {
    const normalizedExpression = normalizeFormulaExpressionForMath(expression);
    const validation = validateSafeFormulaExpression(normalizedExpression);
    if (!validation.valid) {
        throw new Error(validation.error ?? 'Formula expression is not allowed.');
    }

    const parsed = math.parse(normalizedExpression);
    return (scope: Record<string, unknown>) => parsed.evaluate({
        ...JAVASCRIPT_BUILTINS,
        ...scope
    });
};

export const normalizeFormulaEvaluationResult = (result: unknown): { value: number; unit: string | null } => {
    if (typeof result === 'number' || typeof result === 'boolean') {
        return { value: Number(result), unit: null };
    }

    if (isMathUnit(result)) {
        const simplifiedResult = result.simplify();
        const numericValue = simplifiedResult.toNumber();
        const unitString = simplifiedResult.formatUnits();
        const normalizedUnit = normalizeUnit(unitString);
        return { value: numericValue, unit: normalizedUnit ?? null };
    }

    return { value: Number(result), unit: null };
};

// Define custom units for the profitability calculator
// Currency units (treated as base dimensions)
const defineUnitIfMissing = (name: string, options?: unknown) => {
    try {
        math.unit(1, name);
        return;
    } catch {
        // Unit doesn't exist yet, create below.
    }

    math.createUnit(name, options as any);
};

defineUnitIfMissing('EUR', { aliases: ['euro', 'euros'] });
defineUnitIfMissing('USD', { aliases: ['dollar', 'dollars'] });
defineUnitIfMissing('GBP', { aliases: ['pound_sterling', 'pound'] });
// math.js has "hectare" by default but not always "ha" alias.
defineUnitIfMissing('ha', { definition: '10000 m^2' });

// Note: math.js already includes many standard units including:
// - Area: ha (hectare), acre, m^2, km^2
// - Mass: kg, t (tonne), g, lb, oz
// - Distance: m, km, cm, mm, mi, ft, in
// - Volume: L, mL, m^3, gal
// - Time: s, min, h, day, week, month, year
// - Energy: J, kWh
// - Power: W, kW
// - Compound units are automatically handled by math.js
// Examples: USD/t, EUR/kg, kg/ha, acre, etc.

/**
 * Evaluates an expression with unit awareness.
 * Returns both the numeric value and the resulting unit.
 * 
 * @param expression - The mathematical expression to evaluate
 * @param scope - Variable values with optional units (e.g., { price: { value: 100, unit: 'EUR/t' } })
 * @returns Object with numeric value and unit string
 */
export const evaluateWithUnits = (
    expression: string,
    scope: Record<string, { value: number; unit?: string }>
): { value: number; unit: string | null } => {
    try {
        // Convert scope to math.js units
        const mathScope: Record<string, any> = {};
        for (const [key, { value, unit }] of Object.entries(scope)) {
            if (unit) {
                mathScope[key] = math.unit(value, unit);
            } else {
                mathScope[key] = value;
            }
        }

        // Evaluate the expression
        const result = evaluateSafeFormulaExpression(expression, mathScope);
        return normalizeFormulaEvaluationResult(result);
    } catch (error) {
        throw new Error(`Unit evaluation error: ${(error as Error).message}`);
    }
};

/**
 * Normalizes a unit string by removing spaces around operators.
 * This ensures consistent unit formatting (e.g., "USD / month" -> "USD/month").
 * 
 * @param unitString - The unit string to normalize (e.g., "USD / month", "kg / ha", "m ^ 2")
 * @returns Normalized unit string (e.g., "USD/month", "kg/ha", "m^2"), or undefined if input is empty/null
 */
export const normalizeUnit = (unitString?: string | null): string | undefined => {
    if (!unitString || !unitString.trim()) {
        return undefined;
    }

    // Normalize spaces around operators: "USD / month" -> "USD/month"
    const normalized = unitString.trim()
        .replace(/\s*\/\s*/g, '/')
        .replace(/\s*\*\s*/g, '*')
        .replace(/\s*\^\s*/g, '^')
        .replace(/\s+/g, ' ') // Multiple spaces to single space
        .trim();

    return normalized || undefined;
};

/**
 * Validates that a unit string is valid in our unit system.
 * Supports both simple units (e.g., "kg", "EUR") and compound units with operations (e.g., "kg / ha", "EUR / t", "m^2").
 * 
 * @param unitString - The unit string to validate (e.g., "EUR/t", "kg/ha", "kg / ha", "m^2")
 * @returns true if valid, false otherwise
 */
export const isValidUnit = (unitString: string): boolean => {
    if (!unitString || !unitString.trim()) {
        return false;
    }

    try {
        // Math.js can handle unit expressions with operators
        // Try parsing the unit expression
        math.unit(1, unitString.trim());
        return true;
    } catch {
        // If direct parsing fails, try normalizing spaces around operators
        try {
            // Normalize spaces: "kg / ha" -> "kg/ha" (math.js prefers no spaces)
            const normalized = normalizeUnit(unitString);
            if (!normalized) return false;
            math.unit(1, normalized);
            return true;
        } catch {
            return false;
        }
    }
};

/**
 * Checks if two units are compatible (can be added/subtracted).
 * 
 * @param unit1 - First unit string
 * @param unit2 - Second unit string
 * @returns true if compatible, false otherwise
 */
export const areUnitsCompatible = (unit1: string, unit2: string): boolean => {
    try {
        const u1 = math.unit(1, unit1);
        const u2 = math.unit(1, unit2);
        // Try to add them - will throw if incompatible
        math.add(u1, u2);
        return true;
    } catch {
        return false;
    }
};

/**
 * Gets the resulting unit from an operation.
 * Useful for determining what unit a formula will produce.
 * Units are automatically simplified (e.g., "EUR * g / EUR" becomes "g").
 * 
 * @param expression - Mathematical expression
 * @param scope - Variable units
 * @returns The resulting unit string (simplified), or null if dimensionless
 */
export const inferResultUnit = (
    expression: string,
    scope: Record<string, string | undefined>
): string | null => {
    try {
        const mathScope: Record<string, any> = {};
        let hasUnits = false;
        const uniqueUnits = new Set<string>();

        for (const [key, unit] of Object.entries(scope)) {
            if (unit) {
                mathScope[key] = math.unit(1, unit);
                hasUnits = true;
                uniqueUnits.add(unit);
            } else {
                mathScope[key] = 1;
            }
        }

        // Only infer if at least one input has units
        if (!hasUnits) return null;

        const result = evaluateSafeFormulaExpression(expression, mathScope);

        if (isMathUnit(result)) {
            // Simplify the unit (e.g., "EUR * g / EUR" becomes "g")
            const simplified = result.simplify();
            const unitString = simplified.formatUnits();
            // Normalize the unit string to remove spaces around operators
            return normalizeUnit(unitString) ?? null;
        }

        // In some cases (e.g., math.* helpers), math.js returns a plain number even
        // when all operands carry the same unit. If we still tracked a single unit,
        // propagate it.
        if (typeof result === 'number' && hasUnits && uniqueUnits.size === 1) {
            const unit = Array.from(uniqueUnits)[0];
            // Normalize the unit string to remove spaces around operators
            return normalizeUnit(unit) ?? null;
        }
        return null;
    } catch (error) {
        console.warn(`Failed to infer unit for expression "${expression}":`, error);
        return null;
    }
};

/**
 * Simplifies a unit expression string.
 * 
 * @param unitString - Unit expression to simplify (e.g., "EUR * g / EUR")
 * @returns Simplified unit string (e.g., "g"), or the original string if it can't be simplified
 */
export const simplifyUnit = (unitString: string): string => {
    try {
        const unit = math.unit(1, unitString);
        const simplified = unit.simplify();
        const formatted = simplified.formatUnits();
        // Normalize the unit string to remove spaces around operators
        return normalizeUnit(formatted) ?? formatted;
    } catch {
        // If it's not a valid unit expression, return normalized version of original
        return normalizeUnit(unitString) ?? unitString;
    }
};

/**
 * Parses a node name to extract unit information if present in parentheses.
 * Example: "Oil price (€/t)" -> "EUR/t"
 * Example: "Seed yield (kg/ha)" -> "kg/ha"
 * 
 * @param nodeName - The node name to parse
 * @returns The unit string if found, null otherwise
 */
export const parseUnitFromName = (nodeName: string): string | null => {
    const match = nodeName.match(/\(([^)]+)\)$/);
    if (!match) return null;

    const unitString = match[1].trim();
    // Replace € with EUR for consistency
    const normalizedUnit = unitString.replace(/€/g, 'EUR');

    // Validate it's a real unit
    if (isValidUnit(normalizedUnit)) {
        return normalizedUnit;
    }

    return null;
};

/**
 * Validates that a custom unit identifier follows the correct format.
 * - Must start with a letter
 * - Can contain letters, numbers, and underscores
 * - Cannot be empty
 * 
 * @param identifier - The unit identifier to validate
 * @returns Object with valid flag and optional error message
 */
export const validateUnitIdentifier = (identifier: string): { valid: boolean; error?: string } => {
    if (!identifier || !identifier.trim()) {
        return { valid: false, error: 'Unit identifier cannot be empty' };
    }

    const trimmed = identifier.trim();

    // Must start with a letter
    if (!/^[a-zA-Z]/.test(trimmed)) {
        return { valid: false, error: 'Unit identifier must start with a letter' };
    }

    // Can only contain letters, numbers, and underscores
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmed)) {
        return { valid: false, error: 'Unit identifier can only contain letters, numbers, and underscores' };
    }

    return { valid: true };
};

/**
 * Checks if a unit is already defined in the math.js instance.
 * This includes both built-in units and custom units.
 * 
 * @param unitName - The unit name to check
 * @returns true if the unit is already defined, false otherwise
 */
export const isUnitDefined = (unitName: string): boolean => {
    try {
        // Try to create a unit with this name - if it succeeds, it's already defined
        math.unit(1, unitName);
        return true;
    } catch {
        return false;
    }
};

/**
 * Gets all currently defined units (base + custom).
 * 
 * @returns Array of unit names
 */
export const getAllDefinedUnits = (): string[] => {
    // Get all units from math.js
    // Note: math.Unit.UNITS gives us access to the internal units registry
    const units = (math.Unit as any).UNITS;
    if (!units) return [];

    return Object.keys(units);
};

/**
 * Initializes custom units when a graph is loaded.
 * Ensures all custom units from the graph are registered in math.js.
 * 
 * @param customUnits - Array of custom unit identifiers
 */
export const initializeCustomUnits = (customUnits: string[]): void => {
    if (!customUnits || customUnits.length === 0) {
        return;
    }

    for (const unitName of customUnits) {
        try {
            // Check if unit is already defined
            if (!isUnitDefined(unitName)) {
                // Create as a dimensionless custom unit
                math.createUnit(unitName);
            }
        } catch (error) {
            console.warn(`Failed to initialize custom unit "${unitName}":`, error);
        }
    }
};

/**
 * Creates a custom unit and adds it to the custom units array.
 * Validates the identifier and checks for conflicts.
 * 
 * @param unitName - The unit name to create
 * @param customUnits - Current array of custom units
 * @returns Result object with success flag, optional error, and updated units array
 */
export const createCustomUnit = (
    unitName: string,
    customUnits: string[]
): { success: boolean; error?: string; updatedUnits: string[] } => {
    // Validate identifier format
    const validation = validateUnitIdentifier(unitName);
    if (!validation.valid) {
        return { success: false, error: validation.error, updatedUnits: customUnits };
    }

    const trimmed = unitName.trim();

    // Check if it's already a custom unit
    if (customUnits.includes(trimmed)) {
        return { success: true, updatedUnits: customUnits }; // Already exists, no error
    }

    // Check if it conflicts with a built-in unit
    if (isUnitDefined(trimmed)) {
        return {
            success: false,
            error: `"${trimmed}" conflicts with an existing unit`,
            updatedUnits: customUnits
        };
    }

    // Create the unit in math.js
    try {
        math.createUnit(trimmed);
    } catch (error) {
        return {
            success: false,
            error: `Failed to create unit: ${(error as Error).message}`,
            updatedUnits: customUnits
        };
    }

    // Add to the custom units array
    const updatedUnits = [...customUnits, trimmed];
    return { success: true, updatedUnits };
};

/**
 * Extracts unit identifiers from a unit expression string.
 * Handles compound units like "kg/ha" or "EUR * users / month".
 * 
 * @param unitExpression - The unit expression to parse
 * @returns Array of unit identifiers found in the expression
 */
export const extractUnitIdentifiers = (unitExpression: string): string[] => {
    if (!unitExpression || !unitExpression.trim()) {
        return [];
    }

    // Split by operators and whitespace
    const pattern = /[*/^()\s]+/;
    const parts = unitExpression.split(pattern);

    // Filter out empty strings and numbers
    const identifiers = parts
        .map(p => p.trim())
        .filter(p => p.length > 0 && !/^\d+$/.test(p));

    return identifiers;
};

/**
 * Checks if a unit name represents a dimensionless quantity.
 * Dimensionless units include percentages, ratios, multipliers, and factors.
 * 
 * @param unitName - The unit name to check
 * @returns true if the unit is dimensionless, false otherwise
 */
export const isDimensionlessUnit = (unitName: string): boolean => {
    if (!unitName || !unitName.trim()) {
        return false;
    }

    const normalized = unitName.trim().toLowerCase();

    // Check for common dimensionless patterns
    const dimensionlessPatterns = [
        /^%$/,                    // Just %
        /^percent$/,               // percent
        /^percentage$/,            // percentage
        /^ratio$/,                 // ratio
        /^ratios$/,                // ratios
        /^multiplier$/,            // multiplier
        /^multipliers$/,           // multipliers
        /^factor$/,                // factor
        /^factors$/,               // factors
        /^coefficient$/,           // coefficient
        /^coefficients$/,          // coefficients
        /^index$/,                 // index
        /^indices$/,              // indices
        /^score$/,                 // score
        /^scores$/                 // scores
    ];

    return dimensionlessPatterns.some(pattern => pattern.test(normalized));
};

/**
 * Export the configured math instance for advanced usage
 * Also export mathWithUnits which includes unit-preserving wrappers
 */
export { math, mathWithUnits };
